import React, {useState, useEffect, useContext} from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { FiPlus, FiFileText, FiCheckCircle, FiClock, FiCalendar, FiRepeat, FiX } from 'react-icons/fi';
import { CustomDatePicker } from '../commons/datepicker';
import '../../styles/DogReportsTab.css';
import AssignReportModal from './AssignReportModal';
import {TrainerContext} from "../../context/TrainerContext";
import {useToast} from "../commons/toast/ToastContext";

const DogReportsTab = ( {dogReports = { assignments: [], submissions: [] }, dogId, canFill = false} ) => {
    const { t, i18n } = useTranslation(['dogs', 'common']);
    const { trainer } = useContext(TrainerContext);
    const toast = useToast();
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [finishDate, setFinishDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeSubmissionId, setActiveSubmissionId] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [localDogReports, setLocalDogReports] = useState(dogReports);
    const [fillModalOpen, setFillModalOpen] = useState(false);
    const [selectedFillSubmission, setSelectedFillSubmission] = useState(null);
    const [formAnswers, setFormAnswers] = useState({});

    // Fetch templates for assignment modal
    const fetchTemplates = async () => {
        try {
            const response = await axios.get(`/api/v1/reports/templates/school/${trainer.schoolId}`);
            setTemplates(response.data);
        } catch (err) {
            console.error('Error fetching templates:', err);
            toast.error(t('dogs:reports.fetchTemplatesError'), t('common:error'));
        }
    };

    // Fetch reports data
    const fetchReports = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/v1/reports/dog/${dogId}/reports`);
            setLocalDogReports(response.data);
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError(t('dogs:reports.fetchReportsError'));
            toast.error(t('dogs:reports.fetchReportsError'), t('common:error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    useEffect(() => {
        setLocalDogReports(dogReports);
    }, [dogReports]);

    useEffect(() => {
        if (isFinishModalOpen) {
            const today = new Date();
            setFinishDate(today.toISOString().split('T')[0]);
        }
    }, [isFinishModalOpen]);

    const formatDate = (dateString) => {
        if (!dateString) return t('dogs:reports.notSpecified');
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getRecurrenceLabel = (recurrence) => {
        const recurrenceMap = {
            'ONCE': t('dogs:reports.recurrence.once'),
            'DAILY': t('dogs:reports.recurrence.daily'),
            'EVERY_2_DAYS': t('dogs:reports.recurrence.every2Days'),
            'EVERY_3_DAYS': t('dogs:reports.recurrence.every3Days'),
            'EVERY_4_DAYS': t('dogs:reports.recurrence.every4Days'),
            'EVERY_5_DAYS': t('dogs:reports.recurrence.every5Days'),
            'EVERY_6_DAYS': t('dogs:reports.recurrence.every6Days'),
            'WEEKLY': t('dogs:reports.recurrence.weekly')
        };
        return recurrenceMap[recurrence] || recurrence;
    };

    const handleReportAssigned = async () => {
        await fetchReports();
        toast.success(t('dogs:reports.assignedSuccess'), t('common:success'));
    };

    const handleSubmissionClick = (e, submissionId) => {
        setActiveSubmissionId(activeSubmissionId === submissionId ? null : submissionId);
    };

    const handleFinishAssignment = (assignment) => {
        setSelectedAssignment(assignment);
        setIsFinishModalOpen(true);
    };

    const handleSubmitFinishAssignment = async () => {
        if (!selectedAssignment || !finishDate) {
            toast.warning(t('dogs:reports.selectEndDate'), t('dogs:reports.missingData'));
            return;
        }

        try {
            setLoading(true);
            const requestData = {
                assignmentId: selectedAssignment.id,
                date: finishDate
            };

            await axios.post('/api/v1/reports/assignment/finish', requestData);

            setLocalDogReports(prevState => ({
                ...prevState,
                assignments: prevState.assignments.map(assignment =>
                    assignment.id === selectedAssignment.id
                        ? { ...assignment, endDate: finishDate }
                        : assignment
                )
            }));

            setIsFinishModalOpen(false);
            setSelectedAssignment(null);
            setFinishDate('');
            toast.success(t('dogs:reports.taskFinishedSuccess'), t('common:success'));
        } catch (err) {
            console.error('Error finishing assignment:', err);
            const errorMessage = err.response?.data?.message || t('dogs:reports.finishTaskError');
            toast.error(errorMessage, t('common:error'));
        } finally {
            setLoading(false);
        }
    };

    // --- Fill report handlers (for ownerless dogs) ---

    const handleFillReport = (submission) => {
        setSelectedFillSubmission(submission);
        const initialAnswers = {};
        if (submission.template && submission.template.questions) {
            submission.template.questions.forEach(question => {
                initialAnswers[question.id] = '';
            });
        }
        setFormAnswers(initialAnswers);
        setFillModalOpen(true);
    };

    const handleAnswerChange = (questionId, value) => {
        setFormAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const handleSubmitReport = async () => {
        try {
            setLoading(true);

            const unansweredQuestions = selectedFillSubmission.template.questions.filter(
                question => !formAnswers[question.id] || formAnswers[question.id].trim() === ''
            );

            if (unansweredQuestions.length > 0) {
                toast.warning(t('dogs:reports.answerAllQuestions'));
                setLoading(false);
                return;
            }

            const answers = selectedFillSubmission.template.questions.map(question => ({
                questionId: question.id,
                answerText: formAnswers[question.id] || ''
            }));

            const submissionData = {
                submissionId: selectedFillSubmission.id,
                answers: answers
            };

            await axios.post('/api/v1/reports/submissions/answers', submissionData);

            setFillModalOpen(false);
            setSelectedFillSubmission(null);
            setFormAnswers({});
            setActiveSubmissionId(null);
            toast.success(t('dogs:reports.reportSavedSuccess'), t('common:success'));

            await fetchReports();
        } catch (err) {
            console.error('Error submitting report:', err);
            const errorMessage = err.response?.data?.message || t('dogs:reports.reportSaveError');
            toast.error(errorMessage, t('common:error'));
        } finally {
            setLoading(false);
        }
    };

    // Group submissions by assignment - backend now provides all submissions with status
    const submissionsByAssignment = localDogReports.assignments.map(assignment => {
        const assignmentSubmissions = localDogReports.submissions
            .filter(submission => submission.assignmentId === assignment.id)
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

        return {
            assignment,
            submissions: assignmentSubmissions
        };
    });

    if (loading && localDogReports.assignments.length === 0) return <div>{t('dogs:reports.loadingReports')}</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="dog-reports-tab">
            {submissionsByAssignment.length === 0 ? (
                <div className="dog-reports-header-container">
                    <button
                        className="report-create-btn dog-reports-top-btn"
                        onClick={(e) => {
                            e.preventDefault();
                            setIsAssignModalOpen(true);
                        }}
                    >
                        <FiPlus className="reports-btn-icon" />
                        <span>{t('dogs:reports.assignReport')}</span>
                    </button>
                </div>
            ) : (
                <div className="dog-reports-header-container">
                    <button
                        className="report-create-btn"
                        onClick={(e) => {
                            e.preventDefault();
                            setIsAssignModalOpen(true);
                        }}
                    >
                        <FiPlus className="reports-btn-icon" />
                        <span>{t('dogs:reports.assignReport')}</span>
                    </button>
                </div>
            )}

            {submissionsByAssignment.length === 0 ? (
                <div className="reports-empty-state">
                    <FiFileText className="reports-empty-icon" />
                    <p>{t('dogs:reports.noAssignedReports')}</p>
                    <p className="reports-empty-subtitle">{canFill ? t('dogs:reports.assignToFill') : t('dogs:reports.assignForOwnerToFill')}</p>
                    <button
                        className="report-create-btn"
                        onClick={(e) => {
                            e.preventDefault();
                            setIsAssignModalOpen(true);
                        }}
                    >
                        <FiPlus className="reports-btn-icon" />
                        <span>{t('dogs:reports.assignReport')}</span>
                    </button>
                </div>
            ) : (
                <div className="dog-reports-assignments-list">
                    {submissionsByAssignment.map(({ assignment, submissions }) => {
                        const assignmentEndDate = assignment.endDate ? new Date(assignment.endDate) : null;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        let assignmentHasEnded = false;

                        if (assignmentEndDate) {
                            assignmentEndDate.setHours(23, 59, 59, 999);
                            assignmentHasEnded = today > assignmentEndDate;
                        }

                        return (
                            <div key={assignment.id} className="dog-reports-assignment-section">
                                <div className="dog-reports-assignment-header">
                                    <div className="dog-reports-assignment-info">
                                        <h3 className="dog-reports-assignment-template">{assignment.reportTemplateTitle}</h3>
                                        <div className="dog-reports-assignment-meta">
                                            <span className="dog-reports-recurrence-badge">
                                                <FiRepeat className="dog-reports-meta-icon" />
                                                {getRecurrenceLabel(assignment.recurrence)}
                                            </span>
                                            <span className="dog-reports-date-badge">
                                                <FiCalendar className="dog-reports-meta-icon" />
                                                {t('dogs:reports.started')}: {formatDate(assignment.startDate)}
                                            </span>
                                            {assignment.endDate && (
                                                <span className={`dog-reports-date-badge ${assignmentHasEnded ? 'ended' : 'active'}`}>
                                                    <FiCalendar className="dog-reports-meta-icon" />
                                                    {assignmentHasEnded ? t('dogs:reports.ended') + ':' : t('dogs:reports.willEnd') + ':'} {formatDate(assignment.endDate)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {!assignment.endDate && (
                                        <button
                                            className="reports-finish-assignment-btn"
                                            onClick={() => handleFinishAssignment(assignment)}
                                        >
                                            <FiX className="reports-btn-icon" />
                                            {t('dogs:reports.finish')}
                                        </button>
                                    )}
                                </div>

                                <div className="dog-reports-submissions-timeline">
                                    {submissions.length === 0 ? (
                                        <div className="dog-reports-no-submissions">
                                            {t('dogs:reports.noSubmissionsAvailable')}
                                        </div>
                                    ) : (
                                        <>
                                            <h4 className="dog-reports-timeline-title">{t('dogs:reports.submissionHistory')}:</h4>
                                            <div className="dog-reports-submissions-list">
                                                {submissions.map(submission => {
                                                    // Use server-computed status
                                                    const status = submission.status || 'PENDING';

                                                    return (
                                                        <div
                                                            key={submission.id}
                                                            className={`dog-reports-submission-item ${status.toLowerCase()} ${activeSubmissionId === submission.id ? 'active' : ''}`}
                                                            onClick={(e) => handleSubmissionClick(e, submission.id)}
                                                        >
                                                            <div className="dog-reports-submission-header">
                                                                <div className="dog-reports-status-badge-small">
                                                                    {status === 'FILLED' ? (
                                                                        <>
                                                                            <FiCheckCircle className="dog-reports-status-icon" />
                                                                            <span>{t('dogs:reports.status.filled')}</span>
                                                                        </>
                                                                    ) : status === 'EXPIRED' ? (
                                                                        <>
                                                                            <FiClock className="dog-reports-status-icon" />
                                                                            <span>{t('dogs:reports.status.expired')}</span>
                                                                        </>
                                                                    ) : status === 'PENDING' ? (
                                                                        <>
                                                                            <FiClock className="dog-reports-status-icon" />
                                                                            <span>{t('dogs:reports.status.pending')}</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FiCalendar className="dog-reports-status-icon" />
                                                                            <span>{t('dogs:reports.status.upcoming')}</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <div className="dog-reports-submission-date">
                                                                    <FiCalendar className="dog-reports-date-icon" />
                                                                    {formatDate(submission.dueDate)}
                                                                    {submission.completed && submission.submittedAt && (
                                                                        <span className="dog-reports-submitted-at">
                                                                            ({t('dogs:reports.filledOn')}: {formatDate(submission.submittedAt)})
                                                                        </span>
                                                                    )}
                                                                    {status === 'EXPIRED' && (
                                                                        <span className="dog-reports-expired-note">
                                                                            ({t('dogs:reports.taskEnded')}: {formatDate(assignment.endDate)})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Submission details when clicked */}
                                                            {activeSubmissionId === submission.id && (
                                                                <div className="dog-reports-submission-details">
                                                                    <h4>{t('dogs:reports.reportDetails')}:</h4>

                                                                    {status === 'FILLED' ? (
                                                                        <div className="dog-reports-answers-list">
                                                                            {submission.answers.map((answer, index) => (
                                                                                <div key={answer.id} className="dog-reports-answer-item">
                                                                                    <div className="dog-reports-answer-question">
                                                                                        <span className="dog-reports-question-number">{index + 1}.</span>
                                                                                        <span className="dog-reports-question-text">{answer.questionText}</span>
                                                                                    </div>
                                                                                    <div className="dog-reports-answer-text">
                                                                                        {answer.answerText || <em>{t('dogs:reports.noAnswer')}</em>}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : status === 'EXPIRED' ? (
                                                                        <div className="dog-reports-expired-report">
                                                                            <p>{t('dogs:reports.reportNotFilledTaskEnded', { date: formatDate(assignment.endDate) })}</p>
                                                                        </div>
                                                                    ) : status === 'PENDING' ? (
                                                                        canFill ? (
                                                                            <div className="dog-reports-report-actions">
                                                                                <p>{t('dogs:reports.reportAwaitingFill')}</p>
                                                                                <button
                                                                                    className="dog-reports-fill-report-btn"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleFillReport(submission);
                                                                                    }}
                                                                                >
                                                                                    {t('dogs:reports.fillNow')}
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="dog-reports-no-answers">
                                                                                {t('dogs:reports.reportAwaitingOwnerFill')}
                                                                            </div>
                                                                        )
                                                                    ) : (
                                                                        <div className="dog-reports-no-answers">
                                                                            {t('dogs:reports.reportAvailableInFuture')}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Assign Report Modal */}
            {isAssignModalOpen && (
                <AssignReportModal
                    isOpen={isAssignModalOpen}
                    onClose={() => setIsAssignModalOpen(false)}
                    dogId={dogId}
                    templates={templates}
                    onAssigned={handleReportAssigned}
                />
            )}

            {/* Finish Assignment Modal */}
            {isFinishModalOpen && selectedAssignment && (
                <div className="reports-modal-overlay">
                    <div className="reports-modal dog-reports-finish-assignment-modal">
                        <div className="reports-modal-header">
                            <h2 className="reports-modal-title">{t('dogs:reports.finishTask')}: {selectedAssignment.reportTemplateTitle}</h2>
                            <button
                                className="reports-close-btn"
                                onClick={() => {
                                    setIsFinishModalOpen(false);
                                    setSelectedAssignment(null);
                                    setFinishDate('');
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="reports-modal-content">
                            <p className="dog-reports-finish-description">
                                {t('dogs:reports.finishDescription')}
                            </p>

                            <div className="reports-input-group">
                                <label htmlFor="finishDate">{t('dogs:reports.endDate')}</label>
                                <CustomDatePicker
                                    id="finishDate"
                                    name="finishDate"
                                    value={finishDate}
                                    onChange={(e) => setFinishDate(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]}
                                    required
                                />
                            </div>

                            <div className="dog-reports-assignment-summary">
                                <h4>{t('dogs:reports.taskSummary')}:</h4>
                                <div className="dog-reports-summary-item">
                                    <span className="dog-reports-summary-label">{t('dogs:reports.template')}:</span>
                                    <span className="dog-reports-summary-value">{selectedAssignment.reportTemplateTitle}</span>
                                </div>
                                <div className="dog-reports-summary-item">
                                    <span className="dog-reports-summary-label">{t('dogs:reports.startDate')}:</span>
                                    <span className="dog-reports-summary-value">{formatDate(selectedAssignment.startDate)}</span>
                                </div>
                                <div className="dog-reports-summary-item">
                                    <span className="dog-reports-summary-label">{t('dogs:reports.repetition')}:</span>
                                    <span className="dog-reports-summary-value">{getRecurrenceLabel(selectedAssignment.recurrence)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="reports-modal-footer">
                            <button
                                className="reports-cancel-btn"
                                onClick={() => {
                                    setIsFinishModalOpen(false);
                                    setSelectedAssignment(null);
                                    setFinishDate('');
                                }}
                                disabled={loading}
                            >
                                {t('common:cancel')}
                            </button>
                            <button
                                className="dog-reports-finish-btn reports-save-btn"
                                onClick={handleSubmitFinishAssignment}
                                disabled={loading || !finishDate}
                            >
                                {loading ? t('dogs:reports.finishing') : t('dogs:reports.finishTaskBtn')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Fill Report Modal (for ownerless dogs) */}
            {fillModalOpen && selectedFillSubmission && (
                <div className="reports-modal-overlay">
                    <div className="reports-modal dog-reports-fill-modal">
                        <div className="reports-modal-header">
                            <h2 className="reports-modal-title">{t('dogs:reports.fillReport')}: {selectedFillSubmission.template.title}</h2>
                            <button
                                className="reports-close-btn"
                                onClick={() => {
                                    setFillModalOpen(false);
                                    setSelectedFillSubmission(null);
                                    setFormAnswers({});
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="reports-modal-content">
                            <div className="dog-reports-fill-meta">
                                <span>{t('dogs:reports.reportDate')}: {formatDate(selectedFillSubmission.dueDate)}</span>
                            </div>

                            {selectedFillSubmission.template.questions && selectedFillSubmission.template.questions.length > 0 ? (
                                <div className="dog-reports-fill-questions-list">
                                    {selectedFillSubmission.template.questions
                                        .sort((a, b) => a.orderIndex - b.orderIndex)
                                        .map((question, index) => (
                                            <div key={question.id} className="dog-reports-fill-question-item">
                                                <div className="dog-reports-fill-question-header">
                                                    <div className="dog-reports-fill-question-number">{index + 1}.</div>
                                                    <div className="dog-reports-fill-question-text">{question.questionText}</div>
                                                </div>
                                                <div className="dog-reports-fill-answer-input">
                                                    <textarea
                                                        placeholder={t('dogs:reports.typeAnswer')}
                                                        value={formAnswers[question.id] || ''}
                                                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                        rows="3"
                                                        required
                                                    ></textarea>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                <div className="dog-reports-fill-empty-questions">
                                    {t('dogs:reports.noQuestions')}
                                </div>
                            )}
                        </div>

                        <div className="reports-modal-footer">
                            <button
                                className="reports-cancel-btn"
                                onClick={() => {
                                    setFillModalOpen(false);
                                    setSelectedFillSubmission(null);
                                    setFormAnswers({});
                                }}
                                disabled={loading}
                            >
                                {t('common:cancel')}
                            </button>
                            {selectedFillSubmission.template.questions && selectedFillSubmission.template.questions.length > 0 && (
                                <button
                                    className="reports-save-btn"
                                    onClick={handleSubmitReport}
                                    disabled={loading}
                                >
                                    {loading ? t('common:saving') : t('dogs:reports.saveReport')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DogReportsTab;
